// Site footer — deep navy / logo blue / white. A closing CTA, link columns,
// a live Accra clock, and a giant cropped HIBANE wordmark that a blue spotlight
// follows under the pointer.
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUp, Facebook, Instagram, Linkedin } from "lucide-react";
import { ThemeSwitch } from "./nav/ThemeSwitch";

const socialLinks = [
  {
    name: "YouTube",
    icon: <img src="https://cdn-icons-png.flaticon.com/512/1384/1384060.png" alt="" className="h-4 w-4" />,
    url: "https://www.youtube.com/@HIBANEAI",
  },
  {
    name: "TikTok",
    icon: <img src="/lovable-uploads/f0e52e00-f2f9-4dd1-8cad-8bcacc511e7f.png" alt="" className="h-4 w-4" />,
    url: "https://www.tiktok.com/@hibaneai?_t=ZS-8z1ITDGy7oG&_r=1",
  },
  { name: "Instagram", icon: <Instagram className="h-4 w-4" />, url: "https://www.instagram.com/hibane.ai?igsh=MWFpM3lzbGE3ZDJoZw==" },
  { name: "Facebook", icon: <Facebook className="h-4 w-4" />, url: "https://www.facebook.com/profile.php?id=61579732374409" },
  { name: "LinkedIn", icon: <Linkedin className="h-4 w-4" />, url: "https://www.linkedin.com/company/hibane-ai/" },
];

const COLUMNS: Array<{ title: string; links: Array<{ label: string; to: string }> }> = [
  {
    title: "Explore",
    links: [
      { label: "Home", to: "/home" },
      { label: "About", to: "/about" },
      { label: "Services", to: "/services" },
      { label: "Media Studio", to: "/media" },
      { label: "News", to: "/news" },
      { label: "Contact", to: "/contact" },
    ],
  },
  {
    title: "Solutions",
    links: [
      { label: "HR Desk", to: "/hrdesk" },
      { label: "HIBANE Books", to: "/financedesk" },
      { label: "Voice Agent", to: "/voiceagent" },
      { label: "Business Suite", to: "/suite" },
      { label: "AI Hub", to: "/hub-registration" },
    ],
  },
];

const useAccraTime = () => {
  const [t, setT] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setT(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return t.toISOString().slice(11, 19); // Accra is UTC+0
};

const Footer = () => {
  const word = useRef<HTMLDivElement>(null);
  const clock = useAccraTime();

  const onMove = (e: React.PointerEvent) => {
    const el = word.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${e.clientX - r.left}px`);
    el.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  return (
    <footer
      onPointerMove={onMove}
      className="fx-grain relative z-20 overflow-hidden border-t border-cx-accent/15 bg-cx-bg text-cx-fg"
    >
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_50%_40%_at_50%_100%,rgba(74,138,201,0.18),transparent_70%)]" />

      <div className="relative mx-auto max-w-7xl px-6 pt-20 md:pt-24">
        {/* closing line */}
        <div className="flex flex-col gap-8 border-b border-cx-fg/10 pb-14 md:flex-row md:items-end md:justify-between">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-2xl font-cine text-[clamp(2.2rem,5vw,4.6rem)] font-semibold leading-[0.95] tracking-[-0.045em] text-balance"
          >
            Let&apos;s build what Africa runs on next.
          </motion.h2>
          <Link
            to="/consultation-intro"
            className="inline-flex shrink-0 items-center gap-2 self-start rounded-full bg-[#2A629E] px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#3A78BD] md:self-auto"
          >
            Book a free consultation <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {/* columns */}
        <div className="grid grid-cols-2 gap-10 py-14 md:grid-cols-12">
          {COLUMNS.map((c) => (
            <nav key={c.title} aria-label={c.title} className="md:col-span-2">
              <p className="mb-4 font-hud text-[10px] uppercase tracking-[0.24em] text-cx-link">{c.title}</p>
              <ul className="space-y-2.5">
                {c.links.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className="group inline-flex items-center gap-2 text-[15px] text-cx-fg/75 transition-colors hover:text-cx-fg">
                      <span className="h-px w-0 bg-cx-accent transition-all duration-300 group-hover:w-3" />
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}

          <div className="col-span-2 md:col-span-4 md:col-start-6">
            <p className="mb-4 font-hud text-[10px] uppercase tracking-[0.24em] text-cx-link">Talk to us</p>
            <dl className="space-y-3 text-[15px]">
              {[
                ["Email", "info@hibane.ai", "mailto:info@hibane.ai"],
                ["24hrs support", "+233 596 992 641", "tel:+233596992641"],
                ["WhatsApp / local", "+233 544 049 500", "tel:+233544049500"],
              ].map(([k, v, href]) => (
                <div key={k} className="grid grid-cols-[9rem_1fr] gap-2">
                  <dt className="font-hud text-[10px] uppercase leading-6 tracking-[0.18em] text-cx-muted">{k}</dt>
                  <dd>
                    <a href={href} className="text-cx-fg/85 hover:text-cx-link">{v}</a>
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="col-span-2 md:col-span-3">
            <p className="mb-4 font-hud text-[10px] uppercase tracking-[0.24em] text-cx-link">Studios</p>
            <p className="text-[15px] leading-relaxed text-cx-fg/75">
              Accra (HQ) — 24 Hackman Owusu Agyemang St.
              <br />
              Lagos · Kigali
            </p>
            <p className="mt-4 font-hud text-[11px] tabular-nums uppercase tracking-[0.18em] text-cx-muted">
              <span className="fx-blink mr-2 inline-block h-1.5 w-1.5 -translate-y-px rounded-full bg-cx-accent" />
              Accra {clock} GMT
            </p>
            <div className="mt-6 flex items-center gap-2">
              {socialLinks.map((s) => (
                <a
                  key={s.name}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.name}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-cx-fg/15 text-cx-fg/70 transition-colors hover:border-cx-accent hover:text-cx-fg"
                >
                  {s.icon}
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* legal row */}
        <div className="flex flex-col gap-4 border-t border-cx-fg/10 py-6 font-hud text-[10px] uppercase tracking-[0.18em] text-cx-muted md:flex-row md:items-center md:justify-between">
          <Link to="/admin/login" className="cursor-default">
            © 2026 HIBANE AI Consulting LTD. All rights reserved.
          </Link>
          <div className="flex flex-wrap items-center gap-6">
            <ThemeSwitch variant="pill" />
            <Link to="/privacy" className="hover:text-cx-fg">Privacy</Link>
            <Link to="/terms" className="hover:text-cx-fg">Terms</Link>
            <button
              type="button"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="inline-flex items-center gap-2 hover:text-cx-fg"
            >
              Back to top <ArrowUp className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* giant cropped wordmark with a pointer-following blue spotlight */}
      <div ref={word} aria-hidden className="relative -mb-[4.2vw] select-none pb-24 [--mx:50%] [--my:30%] md:pb-0">
        <div className="font-cine text-center text-[25vw] font-bold leading-[0.78] tracking-[-0.07em] text-transparent [-webkit-text-stroke:1px_rgba(74,138,201,0.28)]">
          HIBANE
        </div>
        <div
          className="absolute inset-0 font-cine text-center text-[25vw] font-bold leading-[0.78] tracking-[-0.07em] text-cx-accent"
          style={{
            WebkitMaskImage: "radial-gradient(circle 22vw at var(--mx) var(--my), #000 0%, transparent 70%)",
            maskImage: "radial-gradient(circle 22vw at var(--mx) var(--my), #000 0%, transparent 70%)",
          }}
        >
          HIBANE
        </div>
      </div>
    </footer>
  );
};

export default Footer;
